import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { Beaker, Plus, RefreshCw, Search, AlertTriangle, FileText, MapPin, Package, Clock, Skull, Flame, AlertCircle, Droplets, Wind, Zap, Leaf } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChemicalInventoryPage = () => {
  const [chemicals, setChemicals] = useState([]);
  const [stats, setStats] = useState(null);
  const [hazardClasses, setHazardClasses] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [sdsReviewNeeded, setSdsReviewNeeded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedChemical, setSelectedChemical] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterHazard, setFilterHazard] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    cas_number: '',
    manufacturer: '',
    product_code: '',
    description: '',
    hazard_classes: [],
    signal_word: 'warning',
    quantity: 0,
    unit: 'gallons',
    location: '',
    expiration_date: '',
    department: ''
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
    fetchHazardClasses();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [chemicalsRes, statsRes, expiringRes, sdsRes] = await Promise.all([
        axios.get(`${API}/chemicals`),
        axios.get(`${API}/chemicals/stats`),
        axios.get(`${API}/chemicals/expiring?days=30`).catch(() => ({ data: { chemicals: [] } })),
        axios.get(`${API}/chemicals/sds/review-needed`).catch(() => ({ data: { chemicals: [] } }))
      ]);
      setChemicals(chemicalsRes.data.chemicals || []);
      setStats(statsRes.data);
      setExpiring(expiringRes.data.chemicals || []);
      setSdsReviewNeeded(sdsRes.data.chemicals || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ title: 'Error', description: 'Failed to load chemicals. Please refresh.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchHazardClasses = async () => {
    try {
      const response = await axios.get(`${API}/chemicals/hazard-classes`);
      setHazardClasses(response.data.classes || []);
    } catch (error) {
      console.error('Error fetching hazard classes:', error);
    }
  };

  const handleCreateChemical = async () => {
    if (!formData.name || !formData.manufacturer || !formData.location) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }
    try {
      const response = await axios.post(`${API}/chemicals`, formData);
      toast({ title: 'Success', description: 'Chemical added to inventory!' });
      setShowCreateDialog(false);
      resetForm();
      // Force refresh after short delay
      setTimeout(() => {
        fetchData();
      }, 500);
    } catch (error) {
      console.error('Error creating chemical:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to add chemical', variant: 'destructive' });
    }
  };

  const handleLogUsage = async (chemicalId, quantity) => {
    try {
      await axios.post(`${API}/chemicals/${chemicalId}/usage`, {
        chemical_id: chemicalId,
        quantity_used: quantity,
        used_by: 'Current User',
        purpose: 'General use'
      });
      toast({ title: 'Success', description: 'Usage logged!' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to log usage', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      cas_number: '',
      manufacturer: '',
      product_code: '',
      description: '',
      hazard_classes: [],
      signal_word: 'warning',
      quantity: 0,
      unit: 'gallons',
      location: '',
      expiration_date: '',
      department: ''
    });
  };

  const toggleHazardClass = (hazardClass) => {
    const current = formData.hazard_classes;
    if (current.includes(hazardClass)) {
      setFormData({...formData, hazard_classes: current.filter(h => h !== hazardClass)});
    } else {
      setFormData({...formData, hazard_classes: [...current, hazardClass]});
    }
  };

  const getHazardIcon = (hazardClass) => {
    const icons = {
      explosive: AlertTriangle,
      flammable: Flame,
      oxidizer: Zap,
      compressed_gas: Wind,
      corrosive: Droplets,
      toxic: Skull,
      irritant: AlertCircle,
      health_hazard: AlertCircle,
      environmental: Leaf
    };
    const Icon = icons[hazardClass] || AlertTriangle;
    return <Icon className="w-4 h-4" />;
  };

  const getHazardColor = (hazardClass) => {
    const colors = {
      explosive: 'bg-red-100 text-red-800 border-red-200',
      flammable: 'bg-orange-100 text-orange-800 border-orange-200',
      oxidizer: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      compressed_gas: 'bg-blue-100 text-blue-800 border-blue-200',
      corrosive: 'bg-purple-100 text-purple-800 border-purple-200',
      toxic: 'bg-gray-100 text-gray-800 border-gray-200',
      irritant: 'bg-amber-100 text-amber-800 border-amber-200',
      health_hazard: 'bg-pink-100 text-pink-800 border-pink-200',
      environmental: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[hazardClass] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      low_stock: 'bg-yellow-100 text-yellow-800',
      out_of_stock: 'bg-red-100 text-red-800',
      expired: 'bg-purple-100 text-purple-800',
      disposed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredChemicals = chemicals.filter(c => {
    const matchesSearch = c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.cas_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesHazard = !filterHazard || filterHazard === 'all' || c.hazard_classes?.includes(filterHazard);
    return matchesSearch && matchesHazard;
  });

  return (
    <div className="space-y-6">
      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span>Loading chemicals...</span>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-lg">
              <Beaker className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Chemical Inventory & SDS</h1>
              <p className="text-sm text-slate-300">Track hazardous materials & Safety Data Sheets</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={fetchData} variant="outline" className="text-white border-white/20 hover:bg-white/10" disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-purple-500 hover:bg-purple-600">
                  <Plus className="w-4 h-4 mr-2" /> Add Chemical
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add Chemical to Inventory</DialogTitle>
                  <DialogDescription>Enter chemical details and hazard information</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label>Chemical Name *</Label>
                      <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Chemical name" />
                    </div>
                    <div className="space-y-2">
                      <Label>CAS Number</Label>
                      <Input value={formData.cas_number} onChange={e => setFormData({...formData, cas_number: e.target.value})} placeholder="e.g., 7732-18-5" />
                    </div>
                    <div className="space-y-2">
                      <Label>Manufacturer *</Label>
                      <Input value={formData.manufacturer} onChange={e => setFormData({...formData, manufacturer: e.target.value})} placeholder="Manufacturer name" />
                    </div>
                    <div className="space-y-2">
                      <Label>Product Code</Label>
                      <Input value={formData.product_code} onChange={e => setFormData({...formData, product_code: e.target.value})} placeholder="SKU or product code" />
                    </div>
                    <div className="space-y-2">
                      <Label>Signal Word</Label>
                      <Select value={formData.signal_word} onValueChange={v => setFormData({...formData, signal_word: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="warning">Warning</SelectItem>
                          <SelectItem value="danger">Danger</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Quantity</Label>
                      <div className="flex gap-2">
                        <Input type="number" value={formData.quantity} onChange={e => setFormData({...formData, quantity: parseFloat(e.target.value) || 0})} className="flex-1" />
                        <Select value={formData.unit} onValueChange={v => setFormData({...formData, unit: v})}>
                          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="gallons">Gallons</SelectItem>
                            <SelectItem value="liters">Liters</SelectItem>
                            <SelectItem value="pounds">Pounds</SelectItem>
                            <SelectItem value="kg">Kilograms</SelectItem>
                            <SelectItem value="units">Units</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Location *</Label>
                      <Input value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} placeholder="Storage location" />
                    </div>
                    <div className="space-y-2">
                      <Label>Department</Label>
                      <Input value={formData.department} onChange={e => setFormData({...formData, department: e.target.value})} placeholder="Department" />
                    </div>
                    <div className="space-y-2">
                      <Label>Expiration Date</Label>
                      <Input type="date" value={formData.expiration_date} onChange={e => setFormData({...formData, expiration_date: e.target.value})} />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>GHS Hazard Classes</Label>
                      <div className="flex flex-wrap gap-2">
                        {hazardClasses.map(h => (
                          <Badge key={h.value} variant={formData.hazard_classes.includes(h.value) ? 'default' : 'outline'} className={`cursor-pointer ${formData.hazard_classes.includes(h.value) ? getHazardColor(h.value) : ''}`} onClick={() => toggleHazardClass(h.value)}>
                            {getHazardIcon(h.value)}
                            <span className="ml-1">{h.label}</span>
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Description</Label>
                      <Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="Additional details" rows={2} />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreateChemical}>Add Chemical</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Total Chemicals</p>
                  <p className="text-2xl font-bold">{stats.total_chemicals}</p>
                </div>
                <Beaker className="w-6 h-6 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Active</p>
                  <p className="text-2xl font-bold text-green-600">{stats.by_status?.active || 0}</p>
                </div>
                <Package className="w-6 h-6 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className={stats.by_status?.low_stock > 0 ? 'border-yellow-200 bg-yellow-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Low Stock</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.by_status?.low_stock || 0}</p>
                </div>
                <AlertTriangle className="w-6 h-6 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className={stats.expired > 0 ? 'border-red-200 bg-red-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Expired</p>
                  <p className="text-2xl font-bold text-red-600">{stats.expired || 0}</p>
                </div>
                <Clock className="w-6 h-6 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card className={stats.sds_review_needed > 0 ? 'border-purple-200 bg-purple-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">SDS Review</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.sds_review_needed || 0}</p>
                </div>
                <FileText className="w-6 h-6 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search & Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input className="pl-10" placeholder="Search chemicals, CAS numbers, manufacturers..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <Select value={filterHazard} onValueChange={setFilterHazard}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Hazard Classes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Hazard Classes</SelectItem>
            {hazardClasses.map(h => (
              <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Warnings */}
      {(expiring.length > 0 || sdsReviewNeeded.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {expiring.length > 0 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-orange-700 flex items-center gap-2 text-base">
                  <Clock className="w-5 h-5" /> Expiring Soon ({expiring.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {expiring.slice(0, 3).map(c => (
                  <div key={c.id} className="p-2 bg-white rounded border text-sm">
                    <p className="font-medium">{c.name}</p>
                    <p className="text-orange-600">{c.days_until_expiration} days remaining</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {sdsReviewNeeded.length > 0 && (
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-purple-700 flex items-center gap-2 text-base">
                  <FileText className="w-5 h-5" /> SDS Review Needed ({sdsReviewNeeded.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {sdsReviewNeeded.slice(0, 3).map(c => (
                  <div key={c.id} className="p-2 bg-white rounded border text-sm">
                    <p className="font-medium">{c.name}</p>
                    <p className="text-purple-600">SDS older than 3 years</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Chemicals Grid */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">All Chemicals</TabsTrigger>
          <TabsTrigger value="flammable">Flammable</TabsTrigger>
          <TabsTrigger value="toxic">Toxic</TabsTrigger>
          <TabsTrigger value="corrosive">Corrosive</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredChemicals.map(chemical => (
              <Card key={chemical.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedChemical(chemical)}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold">{chemical.name}</h3>
                      {chemical.cas_number && <p className="text-xs text-gray-500">CAS: {chemical.cas_number}</p>}
                    </div>
                    <Badge className={getStatusColor(chemical.status)}>{chemical.status?.replace('_', ' ')}</Badge>
                  </div>
                  
                  <div className="flex flex-wrap gap-1 mb-3">
                    {chemical.hazard_classes?.map(h => (
                      <Badge key={h} className={`text-xs ${getHazardColor(h)}`}>
                        {getHazardIcon(h)}
                      </Badge>
                    ))}
                    {chemical.signal_word === 'danger' && (
                      <Badge className="bg-red-500 text-white">DANGER</Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-1 text-gray-600">
                      <Package className="w-4 h-4" />
                      {chemical.quantity} {chemical.unit}
                    </div>
                    <div className="flex items-center gap-1 text-gray-600">
                      <MapPin className="w-4 h-4" />
                      {chemical.location}
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 mt-2">{chemical.manufacturer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          {filteredChemicals.length === 0 && !loading && (
            <Card>
              <CardContent className="p-12 text-center">
                <Beaker className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No chemicals found</h3>
                <p className="text-gray-500 mb-4">Add chemicals to start tracking your inventory</p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" /> Add Chemical
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Chemical Detail Dialog */}
      <Dialog open={!!selectedChemical} onOpenChange={() => setSelectedChemical(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedChemical && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle>{selectedChemical.name}</DialogTitle>
                  <Badge className={getStatusColor(selectedChemical.status)}>
                    {selectedChemical.status?.replace('_', ' ')}
                  </Badge>
                </div>
                {selectedChemical.cas_number && (
                  <DialogDescription>CAS Number: {selectedChemical.cas_number}</DialogDescription>
                )}
              </DialogHeader>
              <div className="space-y-6 py-4">
                {selectedChemical.signal_word === 'danger' && (
                  <div className="p-4 bg-red-100 border border-red-200 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-600">⚠️ DANGER</p>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {selectedChemical.hazard_classes?.map(h => (
                    <Badge key={h} className={`${getHazardColor(h)} px-3 py-1`}>
                      {getHazardIcon(h)}
                      <span className="ml-2">{h.replace('_', ' ')}</span>
                    </Badge>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Manufacturer</p>
                    <p className="font-medium">{selectedChemical.manufacturer}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Product Code</p>
                    <p className="font-medium">{selectedChemical.product_code || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Quantity</p>
                    <p className="font-medium text-lg">{selectedChemical.quantity} {selectedChemical.unit}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium">{selectedChemical.location}</p>
                  </div>
                  {selectedChemical.expiration_date && (
                    <div>
                      <p className="text-sm text-gray-500">Expiration Date</p>
                      <p className="font-medium">{new Date(selectedChemical.expiration_date).toLocaleDateString()}</p>
                    </div>
                  )}
                  {selectedChemical.department && (
                    <div>
                      <p className="text-sm text-gray-500">Department</p>
                      <p className="font-medium">{selectedChemical.department}</p>
                    </div>
                  )}
                </div>

                {selectedChemical.description && (
                  <div>
                    <p className="text-sm text-gray-500">Description</p>
                    <p>{selectedChemical.description}</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => {
                    const qty = prompt('Enter quantity used:');
                    if (qty && !isNaN(parseFloat(qty))) {
                      handleLogUsage(selectedChemical.id, parseFloat(qty));
                      setSelectedChemical(null);
                    }
                  }}>
                    Log Usage
                  </Button>
                  <Button variant="outline" className="flex-1">
                    <FileText className="w-4 h-4 mr-2" /> View SDS
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChemicalInventoryPage;
